import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

import { apiClient } from "../../../../../api/httpClient";
import TablePaginationActions from "../../../../../globals/components/TablePaginationActions";

import DuplicateGroupRow from "./DuplicateGroupRow";
import BulkMergeBar from "./BulkMergeBar";

const ENDPOINTS = {
  users: "/duplicate-accounts/users",
  law_firms: "/duplicate-accounts/law-firms",
  pgs: "/duplicate-accounts/professional-guardians",
};

const MERGE_TYPE = {
  users: "portal_user",
  law_firms: "law_firm",
  pgs: "professional_guardian",
};

/**
 * Shared list/table for the three duplicate-entity tabs. Handles fetching,
 * search, pagination, per-row + bulk merge selection, and reloads after a
 * merge so the user sees the up-to-date duplicate set.
 */
const DuplicateGroupTable = ({ entityType, headingLabel, searchPlaceholder }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState({});

  /** A group is only worth showing if it has at least 2 active accounts to merge. */
  const isMergeable = useCallback((group) => {
    const accs = Array.isArray(group?.accounts) ? group.accounts : [];
    let active = 0;
    for (const a of accs) {
      if (!a?.deleted_at) {
        active += 1;
        if (active >= 2) return true;
      }
    }
    return false;
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchQuery?.trim()) params.search = searchQuery.trim();
      const data = await apiClient(ENDPOINTS[entityType], "GET", {}, params);
      const raw = Array.isArray(data?.groups) ? data.groups : [];
      // Defensive: hide groups whose duplicates are all soft-deleted (e.g. before
      // backend scanner is restarted). The user only cares about groups that
      // still have something to merge.
      setGroups(raw.filter(isMergeable));
    } catch (err) {
      console.error("Failed to fetch duplicate groups", err);
      toast.error("Failed to load duplicate accounts");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, searchQuery, isMergeable]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleSelectionChange = (key, keeperId, loserIds) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (!keeperId || loserIds.length === 0) {
        delete next[key];
      } else {
        next[key] = { keeperId, loserIds };
      }
      return next;
    });
  };

  const selectionsArray = useMemo(
    () => Object.values(selections),
    [selections]
  );

  const handleMerged = (result) => {
    toast.success(result?.message || "Merge completed");
    fetchGroups();
  };

  const pagedGroups = groups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderTableContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      );
    }

    if (groups.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            No duplicate {headingLabel.toLowerCase()} found.
          </Typography>
        </Paper>
      );
    }

    return (
      <>
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                <TableCell padding="checkbox" />
                <TableCell />
                <TableCell>Group</TableCell>
                <TableCell>Keeper</TableCell>
                <TableCell>Losers</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedGroups.map((group) => (
                <DuplicateGroupRow
                  key={group.key}
                  group={group}
                  entityType={entityType}
                  isSelected={!!selections[group.key]}
                  onSelectionChange={handleSelectionChange}
                  onMerged={handleMerged}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={groups.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </>
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <TextField
          label={searchPlaceholder || "Search"}
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{ width: 320 }}
          slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchGroups}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {renderTableContent()}

      <BulkMergeBar
        mergeType={MERGE_TYPE[entityType]}
        selections={selectionsArray}
        onClear={() => setSelections({})}
        onCompleted={() => {
          setSelections({});
          fetchGroups();
        }}
      />
    </Box>
  );
};

DuplicateGroupTable.propTypes = {
  entityType: PropTypes.oneOf(["users", "law_firms", "pgs"]).isRequired,
  headingLabel: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string,
};

export default DuplicateGroupTable;
